import { useApolloClient } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { TraceEventImages } from "../graphql/queries";
import { EventImagesMap, EventImageInfo } from "../types";

export function useEventImages() {
    const client = useApolloClient();
    const [eventImages, setEventImages] = useState<EventImagesMap>({});
    const [loadingEventIds, setLoadingEventIds] = useState<Set<string>>(new Set());
    const fetchedEventIds = useRef<Set<string>>(new Set());
    const pendingEventIds = useRef<Set<string>>(new Set());

    const fetchEventImages = useCallback(async (eventIds: string[]) => {
        const newEventIds = eventIds.filter(
            id => !fetchedEventIds.current.has(id) && !pendingEventIds.current.has(id)
        );

        if (newEventIds.length === 0) return;

        newEventIds.forEach(id => pendingEventIds.current.add(id));
        setLoadingEventIds(prev => new Set([...prev, ...newEventIds]));

        const maxRetries = 2;
        let attempt = 0;
        let success = false;

        while (attempt < maxRetries && !success) {
            try {
                const { data } = await client.query({
                    query: TraceEventImages,
                    variables: {
                        input: {
                            eventIdList: newEventIds,
                            isAuth: false
                        }
                    },
                    fetchPolicy: "network-only",
                });

                const traceProducts = data?.traceEventImages?.traceProducts || [];

                const newEventImages: EventImagesMap = {};
                traceProducts.forEach((product: { eventId: string; eventImageInfoList: EventImageInfo[] }) => {
                    const firstImage = product.eventImageInfoList?.[0]?.presignedUrl || null;
                    newEventImages[product.eventId] = firstImage;
                    fetchedEventIds.current.add(product.eventId);
                });

                newEventIds.forEach(id => {
                    if (!newEventImages.hasOwnProperty(id)) {
                        newEventImages[id] = null;
                        fetchedEventIds.current.add(id);
                    }
                });

                setEventImages(prev => ({
                    ...prev,
                    ...newEventImages
                }));

                success = true;
            } catch (error) {
                attempt++;
                if (attempt >= maxRetries) {
                    newEventIds.forEach(id => {
                        fetchedEventIds.current.add(id);
                    });
                }
            }
        }

        newEventIds.forEach(id => pendingEventIds.current.delete(id));
        setLoadingEventIds(prev => {
            const newSet = new Set(prev);
            newEventIds.forEach(id => newSet.delete(id));
            return newSet;
        });
    }, [client]);

    const hasEventImage = useCallback((eventId: string): boolean => {
        return fetchedEventIds.current.has(eventId);
    }, []);

    const isEventLoading = useCallback((eventId: string): boolean => {
        return loadingEventIds.has(eventId);
    }, [loadingEventIds]);

    return {
        eventImages,
        loadingEventIds,
        isEventLoading,
        fetchEventImages,
        hasEventImage
    };
}
